import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import PublicLayout from '../components/public/PublicLayout';
import { PageHero, EmptyState } from './UniversitiesListPage';
import { Award, Calendar, MapPin, ExternalLink, GraduationCap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ScholarshipsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/scholarships?active_only=true`)
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <PublicLayout>
      <PageHero title="Scholarships" subtitle="Live scholarships our students actually qualify for — handpicked weekly." />

      <section className="max-w-7xl mx-auto px-4 py-12">
        {loading ? (
          <p className="text-center text-gray-500">Loading scholarships…</p>
        ) : items.length === 0 ? (
          <div className="bg-gradient-to-br from-[#F5A623]/10 to-white border border-dashed border-[#F5A623]/40 rounded-2xl p-10 text-center" data-testid="scholarships-empty">
            <Award className="w-12 h-12 text-[#F5A623] mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#1A2A4F]">New scholarships are added every week.</h2>
            <p className="mt-2 text-gray-600 max-w-xl mx-auto">Get matched to scholarships you qualify for the moment they go live — drop us a note and we'll keep you posted.</p>
            <Link to="/contact" className="mt-5 inline-block bg-[#F5A623] text-white font-semibold px-6 py-3 rounded-md hover:brightness-95 transition" data-testid="scholarships-empty-cta">
              Get matched
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="scholarships-grid">
            {items.map((s) => (
              <div key={s.id} className="bg-white border border-gray-100 rounded-xl p-6 hover:border-[#F5A623] hover:shadow-lg transition" data-testid={`scholarship-card-${s.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#F5A623]/10 text-[#F5A623] flex items-center justify-center shrink-0">
                    <Award className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-lg text-[#1A2A4F]">{s.name}</h3>
                    <div className="mt-2 flex flex-wrap gap-2 text-xs">
                      {s.amount && <span className="bg-[#F5A623]/15 text-[#F5A623] font-semibold px-2 py-1 rounded">{s.amount}</span>}
                      {s.country && <span className="bg-[#1A2A4F]/5 text-[#1A2A4F] px-2 py-1 rounded inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{s.country}</span>}
                      {s.degree_type && <span className="bg-[#1A2A4F]/5 text-[#1A2A4F] px-2 py-1 rounded inline-flex items-center gap-1"><GraduationCap className="w-3 h-3" />{s.degree_type}</span>}
                      {s.deadline && <span className="bg-red-50 text-red-700 px-2 py-1 rounded inline-flex items-center gap-1"><Calendar className="w-3 h-3" />By {s.deadline}</span>}
                    </div>
                    {s.description && <p className="mt-3 text-sm text-gray-600 line-clamp-3">{s.description}</p>}
                    {s.eligibility && <p className="mt-2 text-xs text-gray-500"><strong>Eligibility:</strong> {s.eligibility}</p>}
                  </div>
                </div>
                <div className="mt-4 flex gap-3">
                  {s.apply_url ? (
                    <a href={s.apply_url} target="_blank" rel="noopener noreferrer" className="bg-[#F5A623] text-white text-sm font-semibold px-4 py-2 rounded-md hover:brightness-95 inline-flex items-center gap-1.5" data-testid={`scholarship-apply-${s.id}`}>
                      Apply <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <Link to="/contact" className="bg-[#F5A623] text-white text-sm font-semibold px-4 py-2 rounded-md hover:brightness-95" data-testid={`scholarship-enquire-${s.id}`}>
                      Enquire
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </PublicLayout>
  );
};

export default ScholarshipsPage;
